/**
 * Role-Based Access Control Utility
 * Handles role hierarchy and access permissions
 * 
 * Role Hierarchy (from highest to lowest):
 * 1. corporate_admin: Can access everything under corporate (all pages)
 * 2. branch_admin: Can access everything EXCEPT: branch, vehicle, designation, staff
 *    - Can access: users, worksheet, departments, sub departments, user settings
 * 3. department_admin: Can access everything branch admin can EXCEPT: department page
 *    - Can access: users, worksheet, user settings, sub departments
 *    - Cannot access: branch, vehicle, designation, staff, departments
 * 4. sub_department_admin: Can access user list, user settings, and worksheet
 *    - Can access: users (user list only), user settings, worksheet
 *    - Cannot access: branch, vehicle, designation, staff, departments, sub departments
 */

/**
 * Role constants
 */
export const ROLES = {
  CORPORATE_ADMIN: 'corporate_admin',
  BRANCH_ADMIN: 'branch_admin',
  DEPARTMENT_ADMIN: 'department_admin',
  SUB_DEPARTMENT_ADMIN: 'sub_department_admin',
  USER: 'user',
  // Legacy role for backward compatibility
  ADMIN: 'admin'
};

/**
 * Normalize role string to handle variations (spaces, underscores, case)
 * Converts "corporate admin" to "corporate_admin" and normalizes to lowercase
 * @param {string} role - The role string to normalize
 * @returns {string} Normalized role string
 */
export const normalizeRole = (role) => {
  if (!role) return '';
  // Convert to lowercase and replace spaces with underscores
  return role.toLowerCase().replace(/\s+/g, '_').trim();
};

/**
 * Check if user has admin-level access (corporate admin or legacy admin)
 * @param {string} userRole - User's role
 * @returns {boolean} True if user has admin access
 */
export const isCorporateAdmin = (userRole) => {
  if (!userRole) return false;
  const role = normalizeRole(userRole);
  return role === ROLES.CORPORATE_ADMIN || role === ROLES.ADMIN;
};

/**
 * Check if user is branch admin
 * @param {string} userRole - User's role
 * @returns {boolean} True if user is branch admin
 */
export const isBranchAdmin = (userRole) => {
  if (!userRole) return false;
  return normalizeRole(userRole) === ROLES.BRANCH_ADMIN;
};

/**
 * Check if user is department admin
 * @param {string} userRole - User's role
 * @returns {boolean} True if user is department admin
 */
export const isDepartmentAdmin = (userRole) => {
  if (!userRole) return false;
  return normalizeRole(userRole) === ROLES.DEPARTMENT_ADMIN;
};

/**
 * Check if user is sub department admin
 * @param {string} userRole - User's role
 * @returns {boolean} True if user is sub department admin
 */
export const isSubDepartmentAdmin = (userRole) => {
  if (!userRole) return false;
  return normalizeRole(userRole) === ROLES.SUB_DEPARTMENT_ADMIN;
};

/**
 * Check if user has any admin role
 * @param {string} userRole - User's role
 * @returns {boolean} True if user has any admin role
 */
export const hasAdminRole = (userRole) => {
  if (!userRole) return false;
  const role = normalizeRole(userRole);
  return (
    role === ROLES.CORPORATE_ADMIN ||
    role === ROLES.BRANCH_ADMIN ||
    role === ROLES.DEPARTMENT_ADMIN ||
    role === ROLES.SUB_DEPARTMENT_ADMIN ||
    role === ROLES.ADMIN // Legacy support
  );
};

/**
 * Check if user can access admin menu items
 * Corporate admin has all access, branch admin has restricted access
 * @param {string} userRole - User's role
 * @returns {boolean} True if user can see admin menu items
 */
export const canAccessAdminMenu = (userRole) => {
  return hasAdminRole(userRole);
};

/**
 * Check if user can access branches page
 * Only corporate admin can access branches
 * @param {string} userRole - User's role
 * @returns {boolean} True if user can access branches
 */
export const canAccessBranches = (userRole) => {
  return isCorporateAdmin(userRole);
};

/**
 * Check if user can access staff page
 * Only corporate admin can access staff
 * @param {string} userRole - User's role
 * @returns {boolean} True if user can access staff
 */
export const canAccessStaff = (userRole) => {
  return isCorporateAdmin(userRole);
};

/**
 * Check if user can access vehicles page
 * Only corporate admin can access vehicles
 * @param {string} userRole - User's role
 * @returns {boolean} True if user can access vehicles
 */
export const canAccessVehicles = (userRole) => {
  return isCorporateAdmin(userRole);
};

/**
 * Check if user can access designations page
 * Only corporate admin can access designations
 * @param {string} userRole - User's role
 * @returns {boolean} True if user can access designations
 */
export const canAccessDesignations = (userRole) => {
  return isCorporateAdmin(userRole);
};

/**
 * Check if user can access users page
 * All admin roles can access users page (hierarchy: corporate > branch > department > sub_department)
 * @param {string} userRole - User's role
 * @returns {boolean} True if user can access users
 */
export const canAccessUsers = (userRole) => {
  return hasAdminRole(userRole);
};

/**
 * Check if user can access worksheet
 * Corporate admin, branch admin, department admin, and sub department admin can access worksheet
 * @param {string} userRole - User's role
 * @returns {boolean} True if user can access worksheet
 */
export const canAccessWorksheet = (userRole) => {
  if (!userRole) return false;
  const role = normalizeRole(userRole);
  // All admin roles (including sub_department_admin) can access worksheet
  return hasAdminRole(userRole);
};

/**
 * Team Work Register — corporate, branch, and department admins only (by role).
 * Menu-granted access is checked separately via route/menu access.
 */
export const canAccessTeamWorkRegister = (userRole) => {
  if (!userRole) return false;
  return (
    isCorporateAdmin(userRole) ||
    isBranchAdmin(userRole) ||
    isDepartmentAdmin(userRole)
  );
};

/**
 * Lock branch + department filters to the viewer's org (department-admin style).
 * Applies to department admins and non-admin users granted team register via Access Control.
 */
export const shouldLockTeamRegisterToUserDepartment = (userRole) => {
  if (!userRole) return false;
  if (isCorporateAdmin(userRole)) return false;
  if (isBranchAdmin(userRole)) return false;
  return true;
};

/**
 * Lock only the branch filter (branch-admin style).
 */
export const shouldLockTeamRegisterBranchOnly = (userRole) => {
  if (!userRole) return false;
  return isBranchAdmin(userRole) && !isDepartmentAdmin(userRole);
};

/**
 * Check if user can access departments page
 * Corporate admin and branch admin can access departments
 * Department admin and sub department admin cannot access departments
 * @param {string} userRole - User's role
 * @returns {boolean} True if user can access departments
 */
export const canAccessDepartments = (userRole) => {
  if (!userRole) return false;
  const role = normalizeRole(userRole);
  // Only corporate admin and branch admin can access
  return role === ROLES.CORPORATE_ADMIN || role === ROLES.BRANCH_ADMIN || role === ROLES.ADMIN;
};

/**
 * Check if user can access sub departments page
 * Corporate admin, branch admin, and department admin can access sub departments
 * Sub department admin cannot access sub departments
 * @param {string} userRole - User's role
 * @returns {boolean} True if user can access sub departments
 */
export const canAccessSubDepartments = (userRole) => {
  if (!userRole) return false;
  const role = normalizeRole(userRole);
  // Corporate admin, branch admin, and department admin can access
  return role === ROLES.CORPORATE_ADMIN || role === ROLES.BRANCH_ADMIN || role === ROLES.DEPARTMENT_ADMIN || role === ROLES.ADMIN;
};

/**
 * Check if user can access user settings page
 * All users (including regular users and sub_department_admin) can access their own user settings
 * @param {string} userRole - User's role
 * @returns {boolean} True if user can access user settings
 */
export const canAccessUserSettings = (userRole) => {
  // All users can access their own user settings page
  return true;
};

/**
 * Get current user role from localStorage
 * @returns {string|null} User role or null if not found
 */
export const getCurrentUserRole = () => {
  try {
    const userData = localStorage.getItem("user");
    if (userData) {
      const parsed = JSON.parse(userData);
      return parsed?.role || null;
    }
  } catch (error) {
    console.error('Error getting user role:', error);
  }
  return null;
};

/**
 * Extract branch IDs from shared branches array
 * Handles both array of IDs and array of objects with id property
 * @param {Array} sharedBranches - Array of branch IDs or branch objects
 * @returns {Array<number>} Array of branch IDs
 */
const extractBranchIds = (sharedBranches) => {
  if (!Array.isArray(sharedBranches) || sharedBranches.length === 0) {
    return [];
  }
  return sharedBranches.map(branch => {
    if (typeof branch === 'number') return branch;
    if (typeof branch === 'string') return parseInt(branch);
    if (branch && typeof branch === 'object' && branch.id) return branch.id;
    return null;
  }).filter(id => id !== null);
};

/**
 * Extract department IDs from shared departments array
 * Handles both array of IDs and array of objects with id property
 * @param {Array} sharedDepartments - Array of department IDs or department objects
 * @returns {Array<number>} Array of department IDs
 */
const extractDepartmentIds = (sharedDepartments) => {
  if (!Array.isArray(sharedDepartments) || sharedDepartments.length === 0) {
    return [];
  }
  return sharedDepartments.map(dept => {
    if (typeof dept === 'number') return dept;
    if (typeof dept === 'string') return parseInt(dept);
    if (dept && typeof dept === 'object' && dept.id) return dept.id;
    return null;
  }).filter(id => id !== null);
};

/**
 * Check if user can edit or delete a document classification
 * 
 * Rules based on sharing status:
 * 
 * 1. Branch-only sharing (shared_with_departments empty, shared_with_branches has values):
 *    - Only corporate admin or branch admin can delete
 *    - Department admin, sub department admin, and normal users CANNOT delete
 * 
 * 2. Both shared (shared_with_departments and shared_with_branches both have values):
 *    - Corporate admin, branch admin, and department admin can delete
 *    - Sub department admin and normal users CANNOT delete
 * 
 * 3. Private (both shared_with_departments and shared_with_branches are empty):
 *    - Sub department admin and normal users can delete only their own private documents
 *    - Corporate admin, branch admin, and department admin can delete any private document
 * 
 * @param {Object} classification - Document classification object
 * @param {string} userRole - Current user's role
 * @param {number|null} userBranchId - Current user's branch ID
 * @param {number|null} userDepartmentId - Current user's department ID
 * @param {number|null} userId - Current user's ID (for checking creator)
 * @param {Array<number>} departmentIdsUnderBranch - Optional: Array of department IDs that belong to the branch (for branch admin check)
 * @returns {boolean} True if user can edit/delete the classification
 */
export const canEditDeleteClassification = (classification, userRole, userBranchId = null, userDepartmentId = null, userId = null, departmentIdsUnderBranch = []) => {
  if (!classification || !userRole) return false;

  const role = normalizeRole(userRole);

  // Corporate admin can edit/delete any classification (no restrictions)
  if (isCorporateAdmin(userRole)) {
    return true;
  }

  // Get shared branches and departments from classification
  const sharedBranches = classification.shared_with_branches || classification.shared_branch_names || [];
  const sharedDepartments = classification.shared_with_departments || classification.shared_department_names || [];
  
  // Extract branch and department IDs
  const branchIds = extractBranchIds(sharedBranches);
  const departmentIds = extractDepartmentIds(sharedDepartments);

  // Determine sharing type
  const hasBranchSharing = branchIds.length > 0;
  const hasDepartmentSharing = departmentIds.length > 0;
  const isBranchOnly = hasBranchSharing && !hasDepartmentSharing;
  const isBothShared = hasBranchSharing && hasDepartmentSharing;
  const isPrivate = !hasBranchSharing && !hasDepartmentSharing;

  // Case 1: Branch-only sharing - only corporate admin or branch admin can delete
  if (isBranchOnly) {
    if (isBranchAdmin(userRole)) {
      // Branch admin can delete if classification is shared with their branch
      if (userBranchId && branchIds.includes(userBranchId)) {
        return true;
      }
    }
    // Department admin, sub department admin, and normal users cannot delete branch-only classifications
    return false;
  }

  // Case 2: Both shared - everyone except sub department admin and normal user can delete
  if (isBothShared) {
    // Sub department admin and normal users cannot delete
    if (isSubDepartmentAdmin(userRole) || role === ROLES.USER) {
      return false;
    }

    // Branch admin: can delete if classification is shared with their branch OR with any department under their branch
    if (isBranchAdmin(userRole)) {
      if (userBranchId) {
        // Check if classification is directly shared with the branch
        if (branchIds.includes(userBranchId)) {
          return true;
        }
        
        // Check if classification is shared with any department that belongs to the branch
        if (departmentIdsUnderBranch.length > 0) {
          const hasSharedDepartmentUnderBranch = departmentIds.some(deptId => 
            departmentIdsUnderBranch.includes(deptId)
          );
          if (hasSharedDepartmentUnderBranch) {
            return true;
          }
        }
      }
      return false;
    }

    // Department admin: can delete if classification is shared with their department
    if (isDepartmentAdmin(userRole)) {
      if (userDepartmentId && departmentIds.includes(userDepartmentId)) {
        return true;
      }
      return false;
    }

    return false;
  }

  // Case 3: Private (both empty) - sub department admin and normal users can delete only their own
  if (isPrivate) {
    // Sub department admin and normal users can only delete their own private documents
    if (isSubDepartmentAdmin(userRole) || role === ROLES.USER) {
      if (userId) {
        const createdById = classification.created_by_id || classification.created_by?.id || classification.creator_id;
        return createdById === userId;
      }
      return false;
    }

    // Branch admin: can delete any private document (they have higher privileges)
    if (isBranchAdmin(userRole)) {
      return true;
    }

    // Department admin: can delete any private document (they have higher privileges)
    if (isDepartmentAdmin(userRole)) {
      return true;
    }

    return false;
  }

  // Default: no access
  return false;
};

/**
 * Get role-based filter parameters for user list APIs
 * Adds appropriate filters based on user role:
 * - branch_admin: adds branch_id filter
 * - department_admin: adds branch_id and department_id filters
 * - sub_department_admin: adds branch_id, department_id, and sub_department_id filters
 * - corporate_admin: no additional filters (can see all)
 * 
 * @param {Function} axiosInstance - Axios instance for API calls
 * @returns {Promise<Object>} Object with filter parameters { branch, department, sub_department }
 */
export const getRoleBasedUserFilters = async (axiosInstance) => {
  try {
    const userRole = getCurrentUserRole();
    
    // Corporate admin sees all users, no filters needed
    if (isCorporateAdmin(userRole)) {
      return {};
    }
    
    // For other admin roles, fetch user details to get their branch/department/sub_department
    const userData = JSON.parse(localStorage.getItem("user"));
    if (!userData?.id) {
      return {};
    }
    
    try {
      const userDetailsRes = await axiosInstance.get(`/auth/details/${userData.id}`);
      const userDetails = userDetailsRes.data;
      
      const branchId = userDetails?.Branch?.id || userDetails?.BranchId;
      const departmentId = userDetails?.Department?.id || userDetails?.DepartmentId;
      const subDepartmentId = userDetails?.SubDepartment?.id || userDetails?.SubDepartmentId;
      
      const filters = {};
      
      // Branch admin: filter by branch
      if (isBranchAdmin(userRole) && branchId) {
        filters.branch = branchId;
      }
      
      // Department admin: filter by branch and department
      if (isDepartmentAdmin(userRole)) {
        if (branchId) {
          filters.branch = branchId;
        }
        if (departmentId) {
          filters.department = departmentId;
        }
      }
      
      // Sub department admin: filter by branch, department, and sub_department
      if (isSubDepartmentAdmin(userRole)) {
        if (branchId) {
          filters.branch = branchId;
        }
        if (departmentId) {
          filters.department = departmentId;
        }
        if (subDepartmentId) {
          filters.sub_department = subDepartmentId;
        }
      }
      
      return filters;
    } catch (error) {
      console.error('Error fetching user details for role-based filters:', error);
      return {};
    }
  } catch (error) {
    console.error('Error getting role-based user filters:', error);
    return {};
  }
};

